import type { PaywallProduct } from '@segmently/schema-models/payments';
import { providerOf, productTabOf, type PaywallProvider } from '@modules/reactFlow/components/onboarding/editorSectionsV2/paywall/productTabOf';

export type ProviderMismatchResult =
  | { mismatch: false }
  | { mismatch: true; candidateProvider: PaywallProvider; otherProvider: PaywallProvider; otherProductIds: string[] };

/** Provider conflict is independent of environment. Return every conflicting bound
 * product so the caller can name the affected rows; never choose a publish winner.
 */
export function detectProviderMismatch(
  candidateProductId: string,
  otherProductIds: readonly string[],
  allProducts: readonly PaywallProduct[],
): ProviderMismatchResult {
  if (!candidateProductId) return { mismatch: false };
  const candidate = allProducts.find(product => product.id === candidateProductId);
  if (!candidate) return { mismatch: false };
  const candidateProvider = providerOf(candidate);
  const conflictingIds = [...new Set(otherProductIds)].filter(id => {
    if (!id) return false;
    const other = allProducts.find(product => product.id === id);
    return other !== undefined && providerOf(other) !== candidateProvider;
  });
  return conflictingIds.length === 0 ? { mismatch: false } : {
    mismatch: true, candidateProvider,
    otherProvider: candidateProvider === 'paddle' ? 'stripe' : 'paddle', otherProductIds: conflictingIds,
  };
}

export type ModeMismatchResult =
  | { mismatch: true; otherMode: 'test' | 'live' }
  | { mismatch: false };

export function detectModeMismatch(
  candidateProductId: string,
  otherProductIds: string[],
  allProducts: PaywallProduct[],
): ModeMismatchResult {
  // No candidate → nothing to mismatch
  if (!candidateProductId) return { mismatch: false };

  const candidate = allProducts.find((p) => p.id === candidateProductId);
  if (!candidate) return { mismatch: false };
  const candidateMode = productTabOf(candidate);
  if (!candidateMode) return { mismatch: false };

  for (const otherId of otherProductIds) {
    if (!otherId) continue; // skip empty placeholders
    const other = allProducts.find((p) => p.id === otherId);
    if (!other || providerOf(other) !== providerOf(candidate)) continue;
    const otherMode = productTabOf(other);
    if (otherMode && otherMode !== candidateMode) {
      return { mismatch: true, otherMode };
    }
  }

  return { mismatch: false };
}
